import { z } from 'zod'
import { DocumentOriginSchema } from './connectors.ts'
import { UsageByAgentSchema } from './engagement.ts'
import {
  AnswerModeSchema,
  ApprovalDecisionSchema,
  IngestionStatusSchema,
  KnowledgeSourceKindSchema,
  ModelAliasSchema,
  RiskClassSchema,
  RoleSchema,
  RunStatusSchema,
  StepStatusSchema,
  StepTypeSchema
} from './enums.ts'
import { GroupRefSchema } from './groups.ts'
import { ChunkKindSchema, IngestReportSchema } from './knowledge-reading.ts'
import { LocaleSchema } from './locale.ts'
import { MessageParamsSchema } from './messages/kinds.ts'
import { CitationSchema, RunOutcomeSchema, StepLabelParamsSchema } from './stream.ts'

// ---------------------------------------------------------------------------
// Canonical error envelope
// ---------------------------------------------------------------------------

/**
 * Every failure -- validation, auth, rate limit, upstream outage -- serializes to this
 * shape. Clients read `issues[].code`, never the HTTP status alone, so a new failure mode
 * never degrades into an unlabelled 500 on the UI.
 *
 * ── `code` versus `messageKey` (PRD-27 D-4) ──
 *
 * `code` is the machine failure class: it drives retry-safety and the HTTP status, and it is
 * coarse on purpose — `not_found` covers 123 different refusals in this API. `messageKey`
 * identifies the SENTENCE, and `params` carries what that sentence interpolates, so a client
 * can render "This knowledge base already holds 500 documents" in the reader's language
 * instead of collapsing it to a generic "Not found."
 *
 * `message` remains, always, and is never removed. It is the operator's line in a log, the
 * answer for a non-UI consumer, and the fallback for a client that does not know a key the
 * server has learned — which is why `messageKey` is a plain string here rather than an enum
 * of the keys this build happens to know about.
 */
export const ApiIssueSchema = z.object({
  code: z.string(),
  message: z.string(),
  /** Stable identifier for the sentence. Absent on failures that have no localized form yet. */
  messageKey: z.string().nullish(),
  /** What the sentence interpolates. Values only -- never a pre-built phrase (D-20). */
  params: MessageParamsSchema.nullish(),
  field: z.string().nullish(),
  retryable: z.boolean()
})
export type ApiIssue = z.infer<typeof ApiIssueSchema>

export const ApiErrorSchema = z.object({
  version: z.literal('v1'),
  status: z.literal('error'),
  requestId: z.string(),
  issues: z.array(ApiIssueSchema).min(1)
})
export type ApiError = z.infer<typeof ApiErrorSchema>

export const PageSchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().nullish()
})

export const paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() })

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

export const AuthProviderSummarySchema = z.object({
  slug: z.string(),
  label: z.string(),
  kind: z.enum(['azure_entra', 'oidc', 'local']),
  iconHint: z.string().nullish()
})
export type AuthProviderSummary = z.infer<typeof AuthProviderSummarySchema>

/**
 * What a person may change about their own account (PRD-27 LOC-003).
 *
 * Both fields optional, so a client that only knows about one of them can send only that. The
 * locale is enum-validated rather than stored as given, for the reason `locale.ts` states.
 */
export const UpdateMeRequestSchema = z
  .object({
    locale: LocaleSchema.optional(),
    /** An IANA zone. Validated against the runtime's own tz database at the route. */
    timeZone: z.string().min(1).max(64).optional()
  })
  .refine((body) => body.locale !== undefined || body.timeZone !== undefined, {
    message: 'nothing to update'
  })
export type UpdateMeRequest = z.infer<typeof UpdateMeRequestSchema>

export const CurrentUserSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  displayName: z.string(),
  roles: z.array(RoleSchema),
  /** The groups this person is in, and the ones they manage (PRD-26 D-3: two different things). */
  groups: z.array(GroupRefSchema),
  managedGroups: z.array(GroupRefSchema),
  providerSlug: z.string(),
  avatarInitials: z.string(),
  /**
   * The account's own language, which is the source of truth for it (PRD-27 LOC-003). The
   * browser mirrors it into localStorage so `boot.js` has something to stamp before the first
   * paint, but this is the copy that follows a person to another device.
   */
  locale: LocaleSchema,
  /**
   * Whether `locale` is a DECISION or a default (PRD-27 D-35).
   *
   * Every account is created with some language, and until a person picks one that value is
   * the deployment's guess, not theirs. A client must treat the two differently: a chosen
   * language follows the person to this device and overrules what the browser asks for; a
   * default does neither. Without this flag a Portuguese reader signing in to an account
   * created before the feature existed would be flipped to the 'en' nobody chose.
   */
  localeChosen: z.boolean(),
  timeZone: z.string(),
  sessionExpiresAt: z.iso.datetime()
})
export type CurrentUser = z.infer<typeof CurrentUserSchema>

export const SessionInfoSchema = z.object({
  authenticated: z.boolean(),
  user: CurrentUserSchema.nullable(),
  csrfToken: z.string().nullable()
})
export type SessionInfo = z.infer<typeof SessionInfoSchema>

// ---------------------------------------------------------------------------
// Conversations and messages
// ---------------------------------------------------------------------------

export const ConversationSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  lastMessagePreview: z.string().nullable(),
  messageCount: z.number().int().nonnegative(),
  pinned: z.boolean(),
  archived: z.boolean(),
  totalCostUsd: z.number().nonnegative(),
  /** The agent this conversation talks to, pinned at creation. */
  agentId: z.string()
})
export type Conversation = z.infer<typeof ConversationSchema>

export const MessageRoleSchema = z.enum(['user', 'assistant', 'system'])
export type MessageRole = z.infer<typeof MessageRoleSchema>

export const MessageSchema = z.object({
  id: z.uuid(),
  conversationId: z.uuid(),
  role: MessageRoleSchema,
  content: z.string(),
  createdAt: z.iso.datetime(),
  runId: z.uuid().nullable(),
  citations: z.array(CitationSchema),
  attachments: z.array(
    z.object({
      id: z.uuid(),
      filename: z.string(),
      mimeType: z.string(),
      sizeBytes: z.number().int().nonnegative(),
      status: IngestionStatusSchema
    })
  ),
  feedback: z.enum(['up', 'down']).nullable()
})
export type Message = z.infer<typeof MessageSchema>

export const CreateConversationRequestSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  /** Which agent to talk to. Omitted means the deployment's default agent. */
  agentId: z.string().min(1).max(64).optional()
})

export const UpdateConversationRequestSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  pinned: z.boolean().optional(),
  archived: z.boolean().optional()
})

export const SendMessageRequestSchema = z.object({
  content: z.string().min(1).max(32_000),
  /** Client-generated so a retried POST resolves to the same run instead of a second one. */
  idempotencyKey: z.string().min(8).max(128).optional(),
  // No `modelAlias` or `answerMode`: model choice and grounding are the agent's (and through
  // it the policy plane's) to decide, never the consumer's per message (AGM-009). Honoring a
  // request `modelAlias` let a consumer pick the model; persisting a request `answerMode`
  // left a latent 'hybrid' that would activate the moment the agent was republished as hybrid.
  attachmentIds: z.array(z.uuid()).optional()
})
export type SendMessageRequest = z.infer<typeof SendMessageRequestSchema>

export const SendMessageAcceptedSchema = z.object({
  runId: z.uuid(),
  conversationId: z.uuid(),
  userMessageId: z.uuid(),
  assistantMessageId: z.uuid(),
  streamUrl: z.string()
})
export type SendMessageAccepted = z.infer<typeof SendMessageAcceptedSchema>

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

export const StepSummarySchema = z.object({
  id: z.uuid(),
  index: z.number().int().nonnegative(),
  type: StepTypeSchema,
  status: StepStatusSchema,
  /** The server's English prose, kept permanently: the operator's line and the fallback (D-8). */
  label: z.string(),
  /**
   * The same caption as a code and its values, so a replayed run reads in the language the
   * person is reading it in TODAY rather than the one the orchestrator wrote it in. Optional
   * and nullable: a step recorded before `0014` has neither, and renders from `label`.
   */
  labelCode: z.string().nullish(),
  labelParams: StepLabelParamsSchema.nullish(),
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().nullable(),
  durationMs: z.number().int().nonnegative().nullable(),
  costUsd: z.number().nonnegative(),
  detail: z.record(z.string(), z.unknown())
})
export type StepSummary = z.infer<typeof StepSummarySchema>

export const RunDetailSchema = z.object({
  id: z.uuid(),
  conversationId: z.uuid(),
  status: RunStatusSchema,
  initiatedBy: z.uuid(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  endedAt: z.iso.datetime().nullable(),
  /** The REQUESTED values; null means the agent decided (see the run's agent snapshot for what actually ran). */
  modelAlias: ModelAliasSchema.nullable(),
  answerMode: AnswerModeSchema.nullable(),
  outcome: RunOutcomeSchema.nullable(),
  steps: z.array(StepSummarySchema),
  totals: z.object({
    modelCalls: z.number().int().nonnegative(),
    toolCalls: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative()
  })
})
export type RunDetail = z.infer<typeof RunDetailSchema>

export const PendingApprovalSchema = z.object({
  id: z.uuid(),
  runId: z.uuid(),
  conversationId: z.uuid(),
  tool: z.string(),
  displayName: z.string(),
  risk: RiskClassSchema,
  digest: z.string(),
  arguments: z.record(z.string(), z.unknown()),
  rationale: z.string(),
  reversible: z.boolean(),
  dataLeavingPlatform: z.array(z.string()),
  requestedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime()
})
export type PendingApproval = z.infer<typeof PendingApprovalSchema>

export const ResolveApprovalRequestSchema = z.object({
  decision: ApprovalDecisionSchema.exclude(['expired']),
  /** Required by the server to match the digest the approver actually saw. */
  digest: z.string(),
  editedArguments: z.record(z.string(), z.unknown()).optional(),
  note: z.string().max(1000).optional()
})
export type ResolveApprovalRequest = z.infer<typeof ResolveApprovalRequestSchema>

// ---------------------------------------------------------------------------
// Knowledge
// ---------------------------------------------------------------------------

/**
 * How a source's documents are read (PRD-24 ING-015).
 *
 * `ocr` decides whether pictures and scans are read: `auto` reads pictures large enough to
 * carry text and every scanned page, `always` reads every picture, `off` reads none. The
 * language list narrows the deployment's OCR languages for this source; it cannot add one
 * the deployment does not have.
 */
export const IngestionPolicySchema = z.object({
  ocr: z.enum(['auto', 'always', 'off']).default('auto'),
  ocrLanguages: z
    .array(z.string().regex(/^[a-z]{3}(_[a-z]+)?$/i, 'a Tesseract language code such as eng, por or spa'))
    .max(6)
    .optional()
})
export type IngestionPolicy = z.infer<typeof IngestionPolicySchema>

export const KnowledgeDocumentSchema = z.object({
  id: z.uuid(),
  sourceId: z.uuid(),
  sourceName: z.string(),
  title: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  status: IngestionStatusSchema,
  statusDetail: z.string().nullable(),
  chunkCount: z.number().int().nonnegative(),
  contentHash: z.string(),
  language: z.string().nullable(),
  pageCount: z.number().int().nonnegative().nullable(),
  createdAt: z.iso.datetime(),
  indexedAt: z.iso.datetime().nullable(),
  parserVersion: z.string(),
  embeddingModel: z.string().nullable(),
  /** From the file's own properties, when it carries them (PRD-24 ING-004, ING-001). */
  author: z.string().nullable(),
  sourceCreatedAt: z.iso.datetime().nullable(),
  /** The extractor × chunker × OCR stamp this row was produced by; null for rows never read by the versioned pipeline. */
  pipelineVersion: z.string().nullable(),
  /** True when a newer pipeline is deployed than the one that read this document. */
  outdated: z.boolean(),
  /** Why a file was set aside rather than read, in words for its maintainers; only on `quarantined` rows. */
  quarantineReason: z.string().nullable(),
  /** What was read and what was skipped; null until the document has been read once. */
  report: IngestReportSchema.nullable(),
  /** Where a synced document came from; null for an upload (PRD-25 D-6). */
  origin: DocumentOriginSchema.nullable()
})
export type KnowledgeDocument = z.infer<typeof KnowledgeDocumentSchema>

export const KnowledgeChunkSchema = z.object({
  id: z.uuid(),
  documentId: z.uuid(),
  index: z.number().int().nonnegative(),
  locator: z.string(),
  heading: z.string().nullable(),
  content: z.string(),
  tokenCount: z.number().int().nonnegative(),
  /** Where the passage came from, in the document's own units (PRD-24 ING-010). */
  page: z.number().int().positive().nullable(),
  slide: z.number().int().positive().nullable(),
  sheet: z.string().nullable(),
  /** The heading hierarchy above it, outermost first: "Policies › Refunds › Exceptions". */
  sectionPath: z.string().nullable(),
  kind: ChunkKindSchema
})
export type KnowledgeChunk = z.infer<typeof KnowledgeChunkSchema>

export const SearchRequestSchema = z.object({
  query: z.string().min(1).max(2000),
  scopeIds: z.array(z.uuid()).optional(),
  limit: z.number().int().min(1).max(50).default(10),
  strategy: z.enum(['hybrid', 'semantic', 'lexical']).default('hybrid')
})

/** Exposed to the search console so a base's maintainers can see the fusion, not just the winner (D-45). */
export const SearchResultSchema = z.object({
  chunkId: z.uuid(),
  documentId: z.uuid(),
  documentTitle: z.string(),
  sourceId: z.uuid(),
  sourceName: z.string(),
  locator: z.string(),
  heading: z.string().nullable(),
  content: z.string(),
  score: z.number(),
  lexicalRank: z.number().int().nullable(),
  lexicalScore: z.number().nullable(),
  semanticRank: z.number().int().nullable(),
  semanticScore: z.number().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  /** Structured position, so a citation can be rendered in the document's own units. */
  page: z.number().int().positive().nullable(),
  slide: z.number().int().positive().nullable(),
  sheet: z.string().nullable(),
  sectionPath: z.string().nullable(),
  kind: ChunkKindSchema
})
export type SearchResult = z.infer<typeof SearchResultSchema>

export const SearchResponseSchema = z.object({
  query: z.string(),
  strategy: z.string(),
  latencyMs: z.number().int().nonnegative(),
  /** Null for a reader who does not maintain the base: arms and ranks are the maintainers' (D-45). */
  lexicalCandidates: z.number().int().nonnegative().nullable(),
  semanticCandidates: z.number().int().nonnegative().nullable(),
  results: z.array(SearchResultSchema)
})
export type SearchResponse = z.infer<typeof SearchResponseSchema>

// ---------------------------------------------------------------------------
// Memory
// ---------------------------------------------------------------------------

export const MemorySchema = z.object({
  id: z.uuid(),
  category: z.enum(['output_preference', 'domain_context', 'working_style']),
  statement: z.string(),
  source: z.enum(['explicit', 'proposed']),
  confidence: z.number().min(0).max(1),
  enabled: z.boolean(),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
  useCount: z.number().int().nonnegative()
})
export type Memory = z.infer<typeof MemorySchema>

export const CreateMemoryRequestSchema = z.object({
  category: z.enum(['output_preference', 'domain_context', 'working_style']),
  statement: z.string().min(3).max(500)
})

// ---------------------------------------------------------------------------
// Admin / observability
// ---------------------------------------------------------------------------

export const ToolDescriptorSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  description: z.string(),
  risk: RiskClassSchema,
  reversible: z.boolean(),
  requiresApproval: z.boolean(),
  enabled: z.boolean(),
  dataLeavingPlatform: z.array(z.string()),
  inputSchema: z.record(z.string(), z.unknown())
})
export type ToolDescriptor = z.infer<typeof ToolDescriptorSchema>

export const ModelRegistryEntrySchema = z.object({
  alias: ModelAliasSchema,
  provider: z.string(),
  model: z.string(),
  displayName: z.string(),
  contextWindow: z.number().int().positive(),
  maxOutputTokens: z.number().int().positive(),
  inputCostPerMTokUsd: z.number().nonnegative(),
  outputCostPerMTokUsd: z.number().nonnegative(),
  cacheReadCostPerMTokUsd: z.number().nonnegative(),
  cacheWriteCostPerMTokUsd: z.number().nonnegative(),
  supportsTools: z.boolean(),
  supportsStreaming: z.boolean(),
  supportsThinking: z.boolean(),
  enabled: z.boolean()
})
export type ModelRegistryEntry = z.infer<typeof ModelRegistryEntrySchema>

export const UsageSummarySchema = z.object({
  windowDays: z.number().int().positive(),
  totals: z.object({
    runs: z.number().int().nonnegative(),
    successfulRuns: z.number().int().nonnegative(),
    failedRuns: z.number().int().nonnegative(),
    messages: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    medianLatencyMs: z.number().int().nonnegative(),
    p95LatencyMs: z.number().int().nonnegative()
  }),
  byModel: z.array(
    z.object({
      model: z.string(),
      calls: z.number().int().nonnegative(),
      inputTokens: z.number().int().nonnegative(),
      outputTokens: z.number().int().nonnegative(),
      costUsd: z.number().nonnegative()
    })
  ),
  byDay: z.array(
    z.object({
      day: z.string(),
      runs: z.number().int().nonnegative(),
      costUsd: z.number().nonnegative()
    })
  ),
  topUsers: z.array(
    z.object({
      userId: z.uuid(),
      displayName: z.string(),
      runs: z.number().int().nonnegative(),
      costUsd: z.number().nonnegative()
    })
  ),
  /**
   * Spend and grounding per agent (AGM-120's admin half).
   *
   * Part of the contract rather than a local widening at the route, because "what did we spend"
   * stopped being answerable without "on whose agent" the moment anybody could build one.
   * Preview runs are excluded, so a maker iterating in the builder never appears as production
   * workload.
   */
  byAgent: z.array(UsageByAgentSchema)
})
export type UsageSummary = z.infer<typeof UsageSummarySchema>

/**
 * Where the deployment's bytes are, and what the vector index costs to keep (OBS-016).
 *
 * Four stores, measured separately because they fill for different reasons and are relieved by
 * different actions: the documents people upload, the passages and embeddings derived from them,
 * the artifacts runs produce on the volume, and the database as a whole. A single "storage used"
 * figure would average all four into a number nobody can act on.
 *
 * Every field is measured rather than modelled. `payloadBytes` is exact arithmetic over real
 * counts (`embeddedCount × dimensions × 4`, the width of a pgvector float4 element); the table
 * and index figures come from `pg_total_relation_size`; the volume figures come from the
 * filesystem and are null when it cannot be read, never zero.
 */
export const StorageUsageSchema = z.object({
  measuredAt: z.iso.datetime(),
  documents: z.object({
    sourceCount: z.number().int().nonnegative(),
    documentCount: z.number().int().nonnegative(),
    indexedCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    bytes: z.number().int().nonnegative(),
    /**
     * Removed documents whose rows and passages are still in the database.
     *
     * Deletion is soft and leaves the passages in place so issued citations still resolve
     * (`base-service.ts`), which means this is real occupancy an operator can reclaim — and the
     * only number here that is a decision rather than a reading.
     */
    reclaimableCount: z.number().int().nonnegative(),
    reclaimableBytes: z.number().int().nonnegative()
  }),
  vectors: z.object({
    passageCount: z.number().int().nonnegative(),
    embeddedCount: z.number().int().nonnegative(),
    dimensions: z.number().int().positive(),
    payloadBytes: z.number().int().nonnegative(),
    tableBytes: z.number().int().nonnegative(),
    indexBytes: z.number().int().nonnegative(),
    totalBytes: z.number().int().nonnegative()
  }),
  database: z.object({
    name: z.string(),
    totalBytes: z.number().int().nonnegative()
  }),
  artifacts: z.object({
    artifactCount: z.number().int().nonnegative(),
    bytes: z.number().int().nonnegative(),
    /** The volume the artifact directory sits on. Null when the filesystem could not be read. */
    volume: z
      .object({
        path: z.string(),
        totalBytes: z.number().int().nonnegative(),
        freeBytes: z.number().int().nonnegative()
      })
      .nullable()
  }),
  /** The limits an upload is actually refused against, so a meter can be drawn against them (PRD-26 D-41). */
  allowance: z.object({
    basesPerMaker: z.number().int().positive(),
    documentsPerBase: z.number().int().positive(),
    storagePerBaseBytes: z.number().int().positive(),
    basesPerGroup: z.number().int().positive(),
    documentsPerGroupBase: z.number().int().positive(),
    storagePerGroupBaseBytes: z.number().int().positive(),
    uploadMaxBytes: z.number().int().positive()
  }),
  bySource: z.array(
    z.object({
      sourceId: z.uuid(),
      /**
       * Null when the reader cannot use the base and is not an administrator: an operator sees
       * what a base occupies and who to ask, never what a base they cannot open is called
       * (PRD-26 D-11 — nobody reads every base except an administrator).
       */
      name: z.string().nullable(),
      /** A person's name, or the owning group's. */
      ownerName: z.string(),
      ownerKind: z.enum(['user', 'group']),
      kind: KnowledgeSourceKindSchema,
      /** The storage limit uploads to this base are refused at; null when it has none (an administrator's own). */
      storageLimitBytes: z.number().int().positive().nullable(),
      documentCount: z.number().int().nonnegative(),
      bytes: z.number().int().nonnegative(),
      passageCount: z.number().int().nonnegative(),
      vectorPayloadBytes: z.number().int().nonnegative(),
      lastIngestedAt: z.iso.datetime().nullable()
    })
  )
})
export type StorageUsage = z.infer<typeof StorageUsageSchema>

export const AuditEventSchema = z.object({
  id: z.uuid(),
  at: z.iso.datetime(),
  actorId: z.uuid().nullable(),
  actorLabel: z.string(),
  action: z.string(),
  resourceType: z.string(),
  resourceId: z.string().nullable(),
  outcome: z.enum(['allow', 'deny', 'error']),
  detail: z.record(z.string(), z.unknown())
})
export type AuditEvent = z.infer<typeof AuditEventSchema>

export const HealthResponseSchema = z.object({
  status: z.enum(['ok', 'degraded', 'fail']),
  service: z.string(),
  version: z.string(),
  uptimeSeconds: z.number().nonnegative(),
  checks: z.record(z.string(), z.enum(['pass', 'fail', 'skip']))
})
export type HealthResponse = z.infer<typeof HealthResponseSchema>
